import React from 'react';
// eslint-disable-next-line no-restricted-imports
import { NavLink, NavLinkProps, useLocation } from 'react-router-dom';
import MuiLink from '@mui/material/Link';
import { PACK } from '../../../submodules/web-shared/consts/packs';
import { getExternalLinkHref, getInternalLink, isInternalLink } from './utils';
import { useMemorizedLink } from './hooks/useMemorizedLink';

export interface LinkProps extends Omit<NavLinkProps, 'className'> {
  pack?: PACK;
  className?: string;
  testID?: string;
  /**
   * if true, the current location will be memorized in the state
   */
  memorizeCurrentLocation?: boolean;
  /**
   * If true and the memorized location present in the state, it will be used instead of the default link
   */
  preferMemorizedLocation?: boolean;
  /**
   * If true and previously memorized location present in the state, it will be passed to the link as the state 
   */
  persistMemorizedLocation?: boolean;
}
interface LinkComponentProps {
  renderContent: (className?: string) => React.ReactElement;
}

const ForwardedLink = React.forwardRef<HTMLElement, LinkComponentProps & { className?: string }>(({ renderContent, ...props }, _ref) => {
  return renderContent(props.className);
});

const LinkComponent = React.forwardRef<HTMLElement, LinkComponentProps>(({ renderContent }, ref) => {
  return (
    <MuiLink
      component={ForwardedLink}
      renderContent={renderContent}
      ref={ref}
    />
  );
});

const Link = ({
  pack,
  testID,
  className,
  memorizeCurrentLocation,
  preferMemorizedLocation,
  persistMemorizedLocation,
  ...props
}: LinkProps) => {
  const location = useLocation();
  const memorization = useMemorizedLink();
  const internalLink = pack === undefined || isInternalLink(pack);

  const renderContent = (muiClassName?: string) => {
    const linkClassName = className ? `${className} ${muiClassName}` : muiClassName;

    const { to, isActive: _, style: __, ...rest } = props;

    if (internalLink) {

      if (persistMemorizedLocation) {
        const to = memorization.persistMemorizedLocation(props.to);
        return <NavLink to={to} {...rest} data-test-id={testID} className={linkClassName} />;
      }

      if (preferMemorizedLocation) {
        const to = memorization.getMemorizedLink(props.to);
        return <NavLink to={to} {...rest} data-test-id={testID} className={linkClassName} />;
      }

      const to = memorizeCurrentLocation ?
        memorization.memorizeCurrentLocation(props.to) :
        getInternalLink(props.to, location);
      return <NavLink to={to} {...rest} data-test-id={testID} className={linkClassName} />;
    }

    const { exact: _exact, ...nativeLinkProps } = rest;

    return (
      <a
        data-test-id={testID}
        href={getExternalLinkHref(pack, to, location)}
        className={linkClassName}
        {...nativeLinkProps} />
    );
  };

  if (typeof props.children === 'string') {
    return <LinkComponent renderContent={renderContent} />;
  }
  return renderContent();

};

export default Link;